import { z } from 'zod';
import {
  countryCodeSchema,
  currencyCodeSchema,
  isoDateSchema,
  localeSchema,
  positiveDecimalSchema,
} from './common';

export const adminUserListQuerySchema = z.object({
  q: z.string().max(120).optional(),
  isAdmin: z.coerce.boolean().optional(),
  includeDeleted: z.coerce.boolean().optional().default(false),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(20),
});
export type AdminUserListQuery = z.infer<typeof adminUserListQuerySchema>;

export const adminSetAdminSchema = z.object({
  isAdmin: z.boolean(),
});
export type AdminSetAdminInput = z.infer<typeof adminSetAdminSchema>;

export const adminTriggerExchangeRateSyncSchema = z.object({
  base: currencyCodeSchema.optional(),
  symbols: z.array(currencyCodeSchema).min(1).optional(),
  date: isoDateSchema.optional(),
});
export type AdminTriggerExchangeRateSyncInput = z.infer<typeof adminTriggerExchangeRateSyncSchema>;

export const adminUpsertExchangeRateSchema = z.object({
  fromCurrency: currencyCodeSchema,
  toCurrency: currencyCodeSchema,
  rate: positiveDecimalSchema,
  date: isoDateSchema,
});
export type AdminUpsertExchangeRateInput = z.infer<typeof adminUpsertExchangeRateSchema>;

export const siteSettingValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.record(z.unknown()),
  z.array(z.unknown()),
]);

export const adminUpsertSiteSettingSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9_.-]+$/i, 'Geçerli ayar anahtarı'),
  value: siteSettingValueSchema,
  description: z.string().max(500).optional(),
});
export type AdminUpsertSiteSettingInput = z.infer<typeof adminUpsertSiteSettingSchema>;

export const adminUpdateUserSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  email: z.string().email().max(255).optional(),
  locale: localeSchema.optional(),
  timezone: z.string().min(1).max(64).optional(),
  baseCurrency: currencyCodeSchema.optional(),
  countryCode: countryCodeSchema.nullable().optional(),
  taxId: z.string().max(64).nullable().optional(),
  theme: z.enum(['light', 'dark', 'system']).optional(),
  isAdmin: z.boolean().optional(),
  isBanned: z.boolean().optional(),
  emailVerifiedAt: z.string().datetime().nullable().optional(),
});
export type AdminUpdateUserInput = z.infer<typeof adminUpdateUserSchema>;

export const adminCreateUserSchema = z
  .object({
    name: z.string().min(1).max(120),
    email: z.string().email().max(255),
    password: z.string().min(8).max(255).optional(),
    sendInvite: z.boolean().default(true),
    locale: localeSchema.optional(),
    timezone: z.string().min(1).max(64).optional(),
    baseCurrency: currencyCodeSchema.optional(),
    countryCode: countryCodeSchema.optional(),
    isAdmin: z.boolean().default(false),
  })
  .refine((d) => d.sendInvite || (d.password && d.password.length >= 8), {
    message: 'Davet kapalı olduğunda en az 8 karakterli bir parola gerekli',
    path: ['password'],
  });
export type AdminCreateUserInput = z.infer<typeof adminCreateUserSchema>;

export const adminResetPasswordSchema = z.object({
  password: z.string().min(8).max(255).optional(),
});
export type AdminResetPasswordInput = z.infer<typeof adminResetPasswordSchema>;
